import i18n from "../../../i18n";import { useEffect, useMemo, useState } from "react";

import { AppLayout, PageBackLink } from "../../../components/layout";
import { ContentCard, EmptyState, LoadingState, PageHeader, ui } from "../../../components/ui";
import { cn } from "../../../lib/utils";
import { HolidayFilterBar } from "../components";
import { Inbox, Plus, CalendarDays } from "lucide-react";
import {
  fetchHoliday,
  fetchEventsPage
} from "../../../services/holidayService";
import { fetchDepartments, fetchWorkLocations } from "../../../services/holidayService";

function formatDate(dateStr) {
  const d = new Date(dateStr);
  const months = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec"];

  return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
}

export function ESSHolidaysPage() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, updateSearch] = useState("");
   const [eventPage, setEventPage] = useState(0);
   const [hasMoreEvents, setHasMoreEvents] = useState(false);
   const setSearch = (value) => { updateSearch(value); setEventPage(0); };

  const [activeSection, updateActiveSection] = useState("Holidays");
  const setActiveSection = (value) => { updateActiveSection(value); setEventPage(0); };
  const [typeFilter, setTypeFilter] = useState("All types");
  const [visibilityFilter, setVisibilityFilter] = useState("All visibility");
  const [holidayTypes, setHolidayTypes] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [locations, setLocations] = useState([]);
  const [scopeFilter, setScopeFilter] = useState("");
  const [loadError, setLoadError] = useState(null);



  useEffect(() => {
    fetchHoliday()
    .then((data) => setHolidayTypes(Array.isArray(data) ? data : data?.data ?? []))
    .catch((error) => {
      setHolidayTypes([]);
      setLoadError(error.message || "Failed to load holidays types.");
    });
  }, []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadError(null);
    const loadEvents = async () => {
      setLoading(true);

      try {
        const result = await fetchEventsPage(search, { page: eventPage });
        const allEvents = result.events;
        if (!active) return;

        const sectionEvents =
          activeSection === "Holidays" ?
          allEvents.filter((event) => event.type === "Public Holiday") :
          allEvents.filter((event) => event.type !== "Public Holiday");

        setEvents((previous) => eventPage === 0 ? sectionEvents : [...previous, ...sectionEvents]);
        setHasMoreEvents(result.hasNext);
      } catch (error) {
        if (!active) return;
        setEvents([]);
        setLoadError(error.message || "Failed to load holidays and events.");
      } finally {
        if (active) setLoading(false);
      }
    };

    const timer = setTimeout(loadEvents, 300);
    return () => { active = false; clearTimeout(timer); };
  }, [activeSection, search, eventPage]);

  useEffect(() => {
    if (visibilityFilter === "Department" && departments.length === 0) {
      fetchDepartments()
      .then((data) => setDepartments(Array.isArray(data) ? data : data?.data ?? []))
      .catch((error) => {
        setDepartments([]);
        setLoadError(error.message || "Failed to load departments.");
      });
    }
    if (visibilityFilter === "Work Location" && locations.length === 0) {
      fetchWorkLocations()
      .then((data) => setLocations(Array.isArray(data) ? data : data?.data ?? []))
      .catch((error) => {
        setLocations([]);
        setLoadError(error.message || "Failed to load departments.");
      });
    }
  }, [visibilityFilter, departments.length, locations.length]);



  const sectionTypeOptions = useMemo(
    () =>
      activeSection === "Holidays" ?
      holidayTypes.filter(
        (type) => type.categoryName === "Public Holiday"
      ) :
      holidayTypes.filter(
        (type) => type.categoryName !== "Public Holiday"
      ),
    [holidayTypes, activeSection]
  );

  const upcomingEvents = useMemo(() => {
    const d = new Date();
    const today = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

    const filtered = events.filter((e) => {
      if (e.start < today) return false;


      const matchType = typeFilter === "All types" || e.type === typeFilter;

      const matchVisibility =
      visibilityFilter === "All visibility" || e.visibility === visibilityFilter;

      return matchType && matchVisibility;
    });

    return filtered.sort((a, b) => new Date(a.start) - new Date(b.start));
  }, [events, typeFilter, visibilityFilter]);


  return (
    <AppLayout>
        {loadError && (
              <div className="mb-4 rounded-xl border border-error/25 bg-error/10 px-4 py-3 text-sm text-error">
                {loadError}
              </div>
            )}
            <PageHeader back={<PageBackLink to="/" />}
        title={i18n.t("dateTime.holidays")}
        />


            <div className="mt-4 border-b border-(--color-text-border)">
        <div className="flex items-center gap-8 px-1">
          <button
            type="button"
            onClick={() => {
              setActiveSection("Holidays");
              setTypeFilter("All types");
            }}
            className={`relative pb-3 text-sm font-semibold transition-colors ${
            activeSection === "Holidays" ?
            "text-primary" :
            "per-file-hint hover:text-foreground"}`
            }>

            Holidays

            {activeSection === "Holidays" &&
            <span className="absolute bottom-0 left-0 right-0 h-[2px] bg-primary" />
            }
          </button>

          <button
            type="button"
            onClick={() => {
              setActiveSection("Events");
              setTypeFilter("All types");
            }}
            className={`relative pb-3 text-sm font-semibold transition-colors ${
            activeSection === "Events" ?
            "text-primary" :
            "per-file-hint hover:text-foreground"}`
            }>

            Events

            {activeSection === "Events" &&
            <span className="absolute bottom-0 left-0 right-0 h-[2px] bg-primary" />
            }
          </button>
        </div>
      </div>

            <HolidayFilterBar
        search={search}
        setSearch={setSearch}
        typeFilter={typeFilter}
        setTypeFilter={setTypeFilter}
        visibilityFilter={visibilityFilter}
        setVisibilityFilter={setVisibilityFilter}
        typeOptions={sectionTypeOptions}
        departments={departments}
        locations={locations}
        scopeFilter={scopeFilter}
        setScopeFilter={setScopeFilter} />





            <ContentCard title={
                                    activeSection === "Holidays"
                                      ? i18n.t("holidays.upcomingHolidays")
                                      : i18n.t("holidays.upcomingEvents")
                                  }>
            {loading ?
        <LoadingState message="Loading upcoming events…" /> :
        upcomingEvents.length === 0 ?
       <EmptyState
         icon={CalendarDays}
         title={
           activeSection === "Holidays"
             ? i18n.t("dateTime.noUpcomingHolidays")
             : i18n.t("dateTime.noUpcomingEvents")
         }
         message={
           activeSection === "Holidays"
             ? "No upcoming holidays for you."
             : "No upcoming events for you."
         } />:


        <div className="flex flex-col gap-4 p-4 sm:p-6">
                {upcomingEvents.map((event) => {
            const isPublic = event.type === "Public Holiday";
            return (
              <div
                key={event.id}
                className={`${ui.card} px-5 sm:px-6 py-5 flex items-start gap-5`}>

                      <div
                  className={cn(
                    "h-12 w-12 rounded-xl grid place-items-center shrink-0",
                    isPublic ?
                    "bg-indigo-50 text-indigo-700" :
                    "bg-rose-50 text-rose-700"
                  )}>

                        <CalendarDays className="h-5 w-5" />
                      </div>
                      <div>
                        <div className="flex flex-wrap items-center gap-3 mb-2">
                          <div className="text-lg font-semibold text-foreground">
                            {event.title}
                          </div>
                          <div
                      className={cn(
                        "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold",
                        isPublic ?
                        "bg-indigo-50 text-indigo-700" :
                        "bg-rose-50 text-rose-700"
                      )}>

                            <span
                        className={cn(
                          "h-1.5 w-1.5 rounded-full",
                          isPublic ? "bg-indigo-500" : "bg-rose-500"
                        )} />

                            {event.type}
                          </div>
                        </div>
                       <div className="text-sm per-file-hint font-medium">
                         <div>
                           {formatDate(event.start)}
                         </div>

                         <div className="text-xs per-file-hint mt-1">
                           {event.startDay === event.endDay
                             ? event.startDay
                             : `${event.startDay} - ${event.endDay}`}
                         </div>

                         {!event.allDay && (
                           <div className="text-xs per-file-hint mt-1">
                             {event.startTime} - {event.endTime}
                           </div>
                         )}
                       </div>
                      </div>
                    </div>);

          })}
              </div>
        }
            </ContentCard>
       {hasMoreEvents && (
         <button type="button" className="btn-secondary" disabled={loading}
           onClick={() => setEventPage((current) => current + 1)}>
           {loading ? "Loading..." : "Load more"}
         </button>
       )}

    </AppLayout>);

}